/**
 * Просьбы агентов о восстановлении — и код, которым владелец их подтверждает.
 *
 * Смысл этого экрана в том, что он **не автоматический**. Программа у машины
 * сайта оставила просьбу; человек у той же машины назвал код голосом; владелец
 * набирает код здесь и подтверждает вторым фактором. Три разных участника, и ни
 * один не может пройти путь в одиночку.
 *
 * Ради этого всё и устроено: право читать копии — самое опасное в системе, и
 * захваченная площадка не должна получать его, сколько бы правильных запросов
 * ни отправила.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import { agoWords } from '../lib/rate.ts';

interface Ask {
  requestId: string;
  hostId: string;
  siteId: string;
  what: 'files' | 'paths' | 'db';
  paths: string[];
  into: string;
  snapshotId: string | null;
  why: string;
  state: string;
  createdAt: string;
  expiresAt: string;
  restoreId: string | null;
}

const WHAT: Record<string, string> = {
  files: 'весь снимок',
  paths: 'отдельные пути',
  db: 'дамп базы',
};

export function RestoreRequests(): React.JSX.Element | null {
  const { data, reload } = useQuery<{ items: Ask[] }>('restore.requests');
  /*
   * Список сам себя обновляет.
   *
   * Без этого карточка застывала на момент загрузки страницы: владелец сидел у
   * консоли час, оставил три просьбы, и всё это время видел ПЕРВУЮ. Вводил код
   * от третьей, получал «код не совпал» и не мог понять, ошибся он кодом или
   * карточкой (их 308). Ошибался ни в том, ни в другом — до нужной карточки
   * просто нельзя было добраться.
   */
  useEffect(() => {
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, []);
  const [code, setCode] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  const items = data?.items ?? [];
  /*
   * Новые сверху: свежая просьба — та, код от которой сейчас в руке.
   *
   * Сервер отдаёт по убыванию времени, но полагаться на чужой порядок в месте,
   * где от порядка зависит попадание в нужную карточку, не стоит.
   */
  const waiting = items.filter((x) => x.state === 'ждёт')
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  /*
   * Решённые показываем недолго и только последние.
   *
   * Просьба — событие дня, а не запись в справочнике: висящий месяцами список
   * отказов превращает экран в архив, мимо которого ходят.
   */
  const recent = items.filter((x) => x.state !== 'ждёт').slice(0, 5);
  if (waiting.length === 0 && recent.length === 0) return null;

  async function decide(id: string, ok: boolean): Promise<void> {
    setBusy(true); setFailure(null); setSaid(null);
    try {
      if (ok) {
        const r = await request<{ restoreId: string }>('restore.request.approve', {
          requestId: id, code: (code[id] ?? '').trim().toUpperCase(),
        });
        setSaid(`Ключи выданы, работа ${r.restoreId} пошла`);
      } else {
        await request('restore.request.deny', { requestId: id });
        setSaid('Отказано');
      }
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="wizard__step asks">
      <h3 className="wizard__title">
        Просьбы машин
        {waiting.length > 0 && <span className="asks__count">{waiting.length}</span>}
      </h3>
      <p className="muted">
        Машина сайта может попросить копию — но получить её она может только из
        ваших рук. Человек у машины называет код, вы вводите его здесь; консоль
        спросит второй фактор и выдаст ключи на чтение <b>одного</b> снимка на
        полчаса.
      </p>

      {failure && <p className="alert alert--error">{failure}</p>}
      {said && <p className="wizard__hint">{said}</p>}

      {waiting.length > 1 && (
        <p className="muted">
          Открытых просьб {waiting.length}. У каждой свой код — вводите его в её
          карточке: код от одной просьбы в карточке другой не подойдёт, и консоль
          так и скажет.
        </p>
      )}

      {waiting.map((a, i) => (
        <div key={a.requestId}
             className={`asks__item${i === 0 ? ' asks__item--first' : ''}`}>
          {i === 0 && waiting.length > 1 && (
            <p className="asks__badge">самая свежая</p>
          )}
          <p className="asks__head">
            <b>{a.siteId}</b>
            <span className="muted"> · {WHAT[a.what] ?? a.what} · в{' '}
              <code className="mono">{a.into}</code>
            </span>
          </p>
          {/*
            «Зачем» — крупно и первым делом.

            Это единственное, что написал человек, и единственное, по чему
            решают. Всё остальное — обстоятельства.
          */}
          <p className="asks__why">{a.why}</p>
          {a.paths.length > 0 && (
            <p className="muted asks__paths mono">
              {a.paths.slice(0, 6).join(', ')}
              {a.paths.length > 6 ? ` … всего ${a.paths.length}` : ''}
            </p>
          )}
          <p className="muted">
            {a.snapshotId
              ? <>снимок <span className="mono">{a.snapshotId.slice(5, 24)}</span></>
              : 'снимок — самый свежий полный'}
            {' · попросили '}{agoWords(Date.now() - new Date(a.createdAt).getTime())}
            {' · '}{(() => {
              const left = new Date(a.expiresAt).getTime() - Date.now();
              if (left <= 0) return 'срок вышел';
              const h = Math.floor(left / 3_600_000);
              return h > 0
                ? `пропадёт через ${h} ч`
                : `пропадёт через ${Math.round(left / 60000)} мин`;
            })()}
          </p>
          <div className="wizard__row">
            <input
              className="input asks__code mono"
              placeholder="код с машины"
              value={code[a.requestId] ?? ''}
              maxLength={12}
              onChange={(e) => setCode((p) => ({ ...p, [a.requestId]: e.target.value }))}
            />
            <button type="button" className="button button--danger"
                    disabled={busy || (code[a.requestId] ?? '').trim().length < 4}
                    onClick={() => { void decide(a.requestId, true); }}>
              Выдать ключи
            </button>
            <button type="button" className="button button--ghost"
                    disabled={busy}
                    onClick={() => { void decide(a.requestId, false); }}>
              Отказать
            </button>
          </div>
        </div>
      ))}

      {recent.map((a) => (
        <p key={a.requestId} className="muted asks__past">
          {a.siteId} · {a.into} · <b>{a.state}</b>
          {a.restoreId && <> · работа <span className="mono">{a.restoreId}</span></>}
          {' · '}{agoWords(Date.now() - new Date(a.createdAt).getTime())}
        </p>
      ))}
    </section>
  );
}
